'use client';

import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Fragment, useEffect, useState } from 'react';

import { City, selectCitiesWithRegion } from '@/shared/config/locations';
import { LoadingSpinner } from '@/shared/ui';

import { useClinics } from '../model';

const cities = selectCitiesWithRegion().sort((a, b) => a.name.localeCompare(b.name));

export default function ComboboxComponent({ isLoading }: { isLoading: boolean }) {
  const { getQueryParams, currentCity } = useClinics();
  const [query, setQuery] = useState('');

  const [selected, setSelected] = useState<(City & { region: string }) | undefined>();

  useEffect(() => {
    if (currentCity) {
      setSelected(cities.find((c) => c?.name === currentCity) ?? cities[0]);
    }
  }, [currentCity]);

  const filteredPeople: Array<City & { region: string }> =
    query === ''
      ? cities
      : cities.filter((city) =>
          city.name
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
        );

  const comboboxChangeHandle = async (param: City & { region: string }) => {
    setSelected(param);
    await getQueryParams({
      ll: `${param.lng},${param.lat}`,
      city: param?.name ?? currentCity ?? 'Минск'
    });
  };

  return (
    <div className="mt-6 w-full text-left md:mt-0 md:w-72">
      <p>Ваш город:</p>
      <Combobox
        disabled={isLoading}
        value={selected ?? cities[0]}
        onChange={(value) => comboboxChangeHandle(value as City & { region: string })}
      >
        <div className="relative z-10 mt-1">
          <div className="relative w-full cursor-default overflow-hidden rounded-lg border-2 bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
            <Combobox.Input
              type="text"
              aria-label="Города"
              className="w-full border-none bg-white py-2 pr-10 pl-3 text-[16px] leading-5 text-gray-900 focus:ring-0 dark:bg-slate-600 dark:text-white"
              displayValue={(city: City & { region: string }) => `${city.name}, ${city.region}`}
              onChange={(event) => setQuery(event.target.value)}
            />
            <Combobox.Button
              aria-label="ФильтрГородов"
              className="absolute inset-y-0 right-0 flex items-center pr-2"
            >
              {isLoading ? (
                <LoadingSpinner size={5} />
              ) : (
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400 dark:text-white"
                  aria-hidden="true"
                />
              )}
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
              {filteredPeople.length === 0 && query !== '' ? (
                <div className="relative cursor-default px-4 py-2 text-gray-700 select-none">
                  Nothing found.
                </div>
              ) : (
                filteredPeople.map((city) => (
                  <Combobox.Option
                    key={city.name}
                    className={({ active }) =>
                      `relative cursor-default py-2 pr-4 pl-10 select-none ${
                        active ? 'bg-yellow-600 text-white' : 'text-gray-900'
                      }`
                    }
                    value={city}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                        >
                          {city.name}
                          <span
                            className={active ? 'text-white' : 'text-gray-400'}
                          >{`, ${city.region}`}</span>
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? 'text-white' : 'text-teal-600'
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
